import React, { useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

export interface DesktopPaginationProps {
  currentPage: number;
  totalPages: number;
  isLoading?: boolean;
  onPageChange: (page: number) => Promise<void>;
}

const buildPageItems = (current: number, total: number): (number | "...")[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | "...")[] = [1];
  const rangeStart = Math.max(2, current - 2);
  const rangeEnd = Math.min(total - 1, current + 2);

  if (rangeStart > 2) pages.push("...");
  for (let p = rangeStart; p <= rangeEnd; p++) pages.push(p);
  if (rangeEnd < total - 1) pages.push("...");

  pages.push(total);
  return pages;
};

export const DesktopPagination: React.FC<DesktopPaginationProps> = ({
  currentPage,
  totalPages,
  isLoading = false,
  onPageChange,
}) => {
  const [hovered, setHovered] = useState<number | string | null>(null);
  const items = buildPageItems(currentPage, totalPages);

  const btnBase: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid #e4e6eb",
    background: "#fff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    color: "#595959",
    transition: "all 0.18s ease",
    userSelect: "none",
    outline: "none",
  };

  const btnActive: React.CSSProperties = {
    ...btnBase,
    background: "#ffffff",
    border: "2px solid #1677ff",
    color: "#1677ff",
    fontWeight: 700,
    boxShadow: "0 0 0 3px rgba(22,119,255,0.12)",
  };

  const btnHover: React.CSSProperties = {
    ...btnBase,
    background: "#f0f5ff",
    border: "1px solid #adc6ff",
    color: "#1677ff",
  };

  const btnDisabled: React.CSSProperties = {
    ...btnBase,
    color: "#bfbfbf",
    cursor: "not-allowed",
    background: "#fafafa",
    border: "1px solid #f0f0f0",
  };

  const getStyle = (item: number | "...", idx: number) => {
    if (item === "...")
      return {
        ...btnBase,
        cursor: "default",
        border: "none",
        background: "transparent",
        color: "#8c8c8c",
      };
    if (item === currentPage) return btnActive;
    if (hovered === idx) return btnHover;
    return btnBase;
  };

  const handleClick = (item: number | "...") => {
    if (item === "..." || item === currentPage || isLoading) return;
    onPageChange(item as number);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "20px 0 8px",
      }}
    >
      {/* Prev */}
      <button
        style={
          currentPage <= 1 || isLoading
            ? btnDisabled
            : hovered === "prev"
              ? btnHover
              : btnBase
        }
        disabled={currentPage <= 1 || isLoading}
        onClick={() => handleClick(currentPage - 1)}
        onMouseEnter={() => setHovered("prev")}
        onMouseLeave={() => setHovered(null)}
        aria-label="前のページ"
      >
        <LeftOutlined style={{ fontSize: 12 }} />
      </button>

      {/* Page numbers */}
      {items.map((item, idx) => (
        <button
          key={idx}
          style={getStyle(item, idx)}
          onClick={() => handleClick(item)}
          onMouseEnter={() =>
            item !== "..." && item !== currentPage && setHovered(idx)
          }
          onMouseLeave={() => setHovered(null)}
          disabled={item === "..." || isLoading}
          aria-label={item === "..." ? undefined : `ページ ${item}`}
          aria-current={item === currentPage ? "page" : undefined}
        >
          {item}
        </button>
      ))}

      {/* Next */}
      <button
        style={
          currentPage >= totalPages || isLoading
            ? btnDisabled
            : hovered === "next"
              ? btnHover
              : btnBase
        }
        disabled={currentPage >= totalPages || isLoading}
        onClick={() => handleClick(currentPage + 1)}
        onMouseEnter={() => setHovered("next")}
        onMouseLeave={() => setHovered(null)}
        aria-label="次のページ"
      >
        <RightOutlined style={{ fontSize: 12 }} />
      </button>
    </div>
  );
};
